import { WifiOff } from 'lucide-react';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { cn } from '@/lib/utils';

/**
 * OfflineBanner Component
 *
 * Displays a fixed banner at the top of the screen when the user is offline.
 * Auto-hides when the user comes back online.
 *
 * Features:
 * - Non-dismissable (auto-hides on reconnect)
 * - Fixed positioning at top of viewport
 * - Warning color scheme (yellow/amber)
 * - Clear messaging about limited functionality
 * - Accessible with proper ARIA attributes
 *
 * @example
 * ```tsx
 * function Layout() {
 *   return (
 *     <div>
 *       <OfflineBanner />
 *       <main>...</main>
 *     </div>
 *   );
 * }
 * ```
 */
export function OfflineBanner() {
  const isOnline = useOnlineStatus();

  // Don't render if online
  if (isOnline) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      aria-atomic="true"
      className={cn(
        'fixed top-0 left-0 right-0 z-50',
        'bg-warning text-white',
        'border-b-2 border-warning',
        'shadow-lg',
        'animate-in slide-in-from-top duration-300'
      )}
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-center gap-3">
          <WifiOff className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
          <p className="text-sm font-medium">
            <span className="font-semibold">Offline mode:</span> You are currently offline. Some
            features may be limited.
          </p>
        </div>
      </div>
    </div>
  );
}
